import React from 'react';
import { ArrowRight, Search, Calendar, AlertTriangle, Info, ThermometerSun } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import GlobalBanner from '@/components/GlobalBanner';
import RelatedArticles from '@/components/RelatedArticles';
import PageSEO from '@/components/PageSEO';
import { ReplacementPageLinks } from '@/components/InternalLinks';

const BRAND_DATA = [
  {
    brand: 'Goodman / Amana',
    method: 'First two digits of the serial number represent the year of manufacture.',
    example: 'Serial: 1401234567 → Manufactured in 2014',
  },
  {
    brand: 'Carrier / Bryant',
    method: 'The 3rd and 4th characters of the serial number indicate the year.',
    example: 'Serial: 2914A12345 → Week 29 of 2014',
  },
  {
    brand: 'Lennox',
    method: 'The first digit is the year, second digit is the month (after 5861 prefix).',
    example: 'Serial: 5861A42345 → 2014, April',
  },
  {
    brand: 'Trane / American Standard',
    method: 'The year is encoded in a letter. Look at the first character after the style number.',
    example: 'Year code letters rotate: Y=1999, A=2000 … N=2013, P=2014',
  },
  {
    brand: 'Rheem / Ruud',
    method: 'The first four digits of the serial number represent the week and year.',
    example: 'Serial: 0514K12345 → 5th week of 2014',
  },
];

const CheckAcAgePage = () => {
  return (
    <div className="min-h-screen bg-white">
      <PageSEO breadcrumbs={[
        { name: 'Home', url: '/' },
        { name: 'How Old Is My AC?', url: '/check-ac-age' },
      ]} />
      <GlobalBanner />
      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-12 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1
            className="text-4xl md:text-5xl font-bold text-[#0F172A] mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="check-ac-age-heading"
          >
            How Old Is My AC Unit?
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-8">
            Your air conditioner's age is one of the most important factors when deciding
            to repair or replace. Here's how to find out exactly how old your system is.
          </p>
          <a href="/instant-hvac-estimate-new-orleans">
            <Button className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-4 px-8 rounded-full text-lg">
              Get an Instant Replacement Estimate
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </a>
        </div>
      </section>

      {/* Lifespan in Louisiana */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-[#0F172A] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            HVAC Lifespan in Louisiana
          </h2>
          <div className="bg-slate-50 rounded-2xl p-8">
            <div className="flex items-start gap-4 mb-6">
              <ThermometerSun className="w-8 h-8 text-[#F97316] flex-shrink-0 mt-1" />
              <div>
                <p className="text-[#0F172A] text-lg font-medium mb-2">
                  In Louisiana, the average lifespan of a central air conditioning unit is 12 to 15 years.
                </p>
                <p className="text-slate-600">
                  High humidity, salt air near the coast, and extreme summer heat put more wear on HVAC systems than
                  in cooler climates. Units in the Greater New Orleans area often run 8+ months per year, which
                  means they degrade faster than the national average.
                </p>
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-4 mt-6">
              <div className="bg-white p-4 rounded-xl text-center">
                <p className="text-3xl font-bold text-[#0EA5E9]">12–15</p>
                <p className="text-sm text-slate-500">Typical Years</p>
              </div>
              <div className="bg-white p-4 rounded-xl text-center">
                <p className="text-3xl font-bold text-[#F97316]">8+</p>
                <p className="text-sm text-slate-500">Months of Use/Year</p>
              </div>
              <div className="bg-white p-4 rounded-xl text-center">
                <p className="text-3xl font-bold text-[#0F172A]">10</p>
                <p className="text-sm text-slate-500">Year Warranty (Avg)</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Serial Number Lookup */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-8">
            <Search className="w-7 h-7 text-[#0EA5E9]" />
            <h2 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Serial Number Lookup by Brand
            </h2>
          </div>
          <p className="text-slate-600 mb-8">
            Every AC unit has a data plate — usually on the side or back of the outdoor condenser unit.
            The serial number on that plate contains the manufacturing date. Here's how to decode it by brand:
          </p>

          <div className="space-y-4">
            {BRAND_DATA.map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-6 border border-slate-200">
                <h3 className="text-lg font-bold text-[#0F172A] mb-2">{item.brand}</h3>
                <p className="text-slate-600 mb-2">{item.method}</p>
                <div className="bg-slate-50 rounded-lg px-4 py-2 text-sm text-slate-500 font-mono">
                  {item.example}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Finding the Data Plate */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-6">
            <Info className="w-7 h-7 text-[#0EA5E9]" />
            <h2 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Where to Find the Data Plate
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="font-bold text-[#0F172A] mb-3 text-lg">Outdoor Unit (Condenser)</h3>
              <ul className="space-y-2 text-slate-600">
                <li className="flex items-start gap-2">
                  <span className="text-[#0EA5E9] font-bold mt-0.5">1.</span>
                  Look at the side panel of your outdoor unit
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#0EA5E9] font-bold mt-0.5">2.</span>
                  Find the silver/white sticker with model and serial numbers
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#0EA5E9] font-bold mt-0.5">3.</span>
                  The serial number is typically 10–15 characters
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-[#0F172A] mb-3 text-lg">Indoor Unit (Air Handler)</h3>
              <ul className="space-y-2 text-slate-600">
                <li className="flex items-start gap-2">
                  <span className="text-[#0EA5E9] font-bold mt-0.5">1.</span>
                  Check the front or side panel of the blower unit
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#0EA5E9] font-bold mt-0.5">2.</span>
                  Data plates may be behind the service access panel
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#0EA5E9] font-bold mt-0.5">3.</span>
                  Record both model and serial number for accurate results
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Warning Signs */}
      <section className="py-12 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-6">
            <AlertTriangle className="w-7 h-7 text-[#F97316]" />
            <h2 className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Signs Your AC May Need Replacing
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              'Unit is over 12 years old',
              'Frequent repairs (2+ per year)',
              'Rising energy bills despite maintenance',
              'Uses R-22 refrigerant (phased out)',
              'Uneven cooling or hot spots in your home',
              'Unusual noises or odors from the system',
            ].map((sign, i) => (
              <div key={i} className="flex items-center gap-3 p-4 bg-white rounded-xl">
                <Calendar className="w-5 h-5 text-[#F97316] flex-shrink-0" />
                <span className="text-[#0F172A] font-medium">{sign}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Internal links / SEO content */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-6">
          <div className="prose prose-slate max-w-none">
            <p>
              Once you know the age of your AC unit, the next step is understanding your replacement options.
              Use our free{' '}
              <a href="/instant-hvac-estimate-new-orleans" className="text-[#0EA5E9]">instant HVAC estimate tool</a>{' '}
              to get pricing in minutes — no phone call required.
            </p>
            <p>
              Not sure if you should replace yet? Read our guide on{' '}
              <a href="/repair-vs-replace-ac" className="text-[#0EA5E9]">repairing vs. replacing your air conditioner</a>{' '}
              or learn more about{' '}
              <a href="/ac-unit-lifespan-louisiana" className="text-[#0EA5E9]">how long AC units last in Louisiana</a>.
            </p>
            <p>
              You can also explore{' '}
              <a href="/ac-replacement-cost-new-orleans" className="text-[#0EA5E9]">AC replacement costs in New Orleans</a>{' '}
              or get{' '}
              <a href="/hvac-pricing-new-orleans" className="text-[#0EA5E9]">HVAC pricing</a>{' '}
              for your specific area.
            </p>
          </div>
        </div>
      </section>

      <RelatedArticles currentPath="/check-ac-age" />

      <ReplacementPageLinks />

      {/* CTA */}
      <section className="py-16 bg-[#0F172A]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Get an Instant HVAC Replacement Estimate
          </h2>
          <p className="text-slate-300 mb-8">
            Know your AC's age? See what a new system would cost — free, fast, and no obligation.
          </p>
          <a href="/instant-hvac-estimate-new-orleans">
            <Button className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-4 px-8 rounded-full text-lg">
              Start My Instant Estimate
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CheckAcAgePage;
